import {
  type UVInsertFaceElement,
  type UVInsertFaceElementLocation,
  type UVInsertFaceElementType,
  isUvInsertUserConfigurableText,
} from "../../models/uv-inserts"

export const UV_INSERT_FACE_TAB_LABELS = {
  front: "Front",
  inside: "Inside",
  back: "Back",
} as const

export type UvInsertFaceRegionTabDescriptor = {
  id: string
  label: string
  /** Locations that belong to this tab (Inside tab covers both inside panels). */
  locations: readonly UVInsertFaceElementLocation[]
}

/**
 * Returns the shared frame type for all face elements. Configs never mix INSERT and GREETINGS_CARD.
 */
export function getUvInsertFrameType(options: {
  faceElements: UVInsertFaceElement[]
}): UVInsertFaceElementType {
  const { faceElements } = options
  if (faceElements.length === 0) {
    throw new Error("getUvInsertFrameType: faceElements must not be empty.")
  }
  const first = faceElements[0].type
  for (let i = 1; i < faceElements.length; i++) {
    if (faceElements[i].type !== first) {
      throw new Error(
        `getUvInsertFrameType: mixed frame types (${first} vs ${faceElements[i].type}) are not supported.`,
      )
    }
  }
  return first
}

/**
 * Ordered region tabs: INSERT → Front, Back; GREETINGS_CARD → Front, Inside, Back.
 */
export function buildUvInsertvisualiserTabs(options: {
  faceElements: UVInsertFaceElement[]
}): UvInsertFaceRegionTabDescriptor[] {
  const { faceElements } = options
  const frameType = getUvInsertFrameType({ faceElements })
  if (frameType === "INSERT") {
    return [
      { id: "front", label: UV_INSERT_FACE_TAB_LABELS.front, locations: ["FRONT"] },
      { id: "back", label: UV_INSERT_FACE_TAB_LABELS.back, locations: ["BACK"] },
    ]
  }
  return [
    { id: "front", label: UV_INSERT_FACE_TAB_LABELS.front, locations: ["FRONT"] },
    {
      id: "inside",
      label: UV_INSERT_FACE_TAB_LABELS.inside,
      locations: ["INSIDE_LEFT", "INSIDE_RIGHT"],
    },
    { id: "back", label: UV_INSERT_FACE_TAB_LABELS.back, locations: ["BACK"] },
  ]
}

const INSIDE_ORDER: UVInsertFaceElementLocation[] = ["INSIDE_LEFT", "INSIDE_RIGHT"]

function sortFacesForTab(
  faces: UVInsertFaceElement[],
  locations: readonly UVInsertFaceElementLocation[],
): UVInsertFaceElement[] {
  const locSet = new Set(locations)
  const filtered = faces.filter((f) => locSet.has(f.location))
  if (locations.includes("INSIDE_LEFT") || locations.includes("INSIDE_RIGHT")) {
    return [...filtered].sort(
      (a, b) =>
        INSIDE_ORDER.indexOf(a.location as (typeof INSIDE_ORDER)[number]) -
        INSIDE_ORDER.indexOf(b.location as (typeof INSIDE_ORDER)[number]),
    )
  }
  return filtered
}

/**
 * Face elements for the selected region tab (from config only; add missing faces in Liquid if a tab is empty).
 */
export function getFaceElementsForRegionTab(options: {
  faceElements: UVInsertFaceElement[]
  tabIndex: number
}): UVInsertFaceElement[] {
  const { faceElements, tabIndex } = options
  if (faceElements.length === 0) {
    return []
  }
  const tabs = buildUvInsertvisualiserTabs({ faceElements })
  const safeIndex = Math.min(Math.max(tabIndex, 0), tabs.length - 1)
  const descriptor = tabs[safeIndex]
  return sortFacesForTab(faceElements, descriptor.locations)
}

/**
 * Tab strip items for UI (id + label). Same order as region tabs.
 */
export function getUvInsertFaceTabItems(options: {
  faceElements: UVInsertFaceElement[]
}): { id: string; label: string }[] {
  const { faceElements } = options
  return buildUvInsertvisualiserTabs({ faceElements }).map(({ id, label }) => ({ id, label }))
}

/**
 * True when the region tab is Front (themes apply only there in the chooser).
 */
export function isUvInsertFrontRegionTab(options: {
  faceElements: UVInsertFaceElement[]
  tabIndex: number
}): boolean {
  const { faceElements, tabIndex } = options
  if (faceElements.length === 0) {
    return false
  }
  const tabs = buildUvInsertvisualiserTabs({ faceElements })
  const safeIndex = Math.min(Math.max(tabIndex, 0), tabs.length - 1)
  return tabs[safeIndex]?.id === "front"
}

/**
 * Line item property keys for IMAGE_UPLOAD elements on the active region tab.
 */
export function getImageUploadPropertyKeysForRegionTab(options: {
  faceElements: UVInsertFaceElement[]
  tabIndex: number
}): string[] {
  const { faceElements, tabIndex } = options
  const faces = getFaceElementsForRegionTab({ faceElements, tabIndex })
  const keys = new Set<string>()
  for (const face of faces) {
    for (const el of face.elementConfig) {
      if (el.type === "IMAGE_UPLOAD" && el.lineItemPropertyKey) {
        keys.add(el.lineItemPropertyKey)
      }
    }
  }
  return [...keys]
}

/**
 * First TEXT element's line item property key on the active region tab, if any.
 */
export function getFirstTextPropertyKeyForRegionTab(options: {
  faceElements: UVInsertFaceElement[]
  tabIndex: number
}): string | null {
  const { faceElements, tabIndex } = options
  const faces = getFaceElementsForRegionTab({ faceElements, tabIndex })
  for (const face of faces) {
    for (const el of face.elementConfig) {
      if (isUvInsertUserConfigurableText(el)) {
        return el.lineItemPropertyKey
      }
    }
  }
  return null
}

/**
 * Line item key that receives suggested-message picks on the active tab.
 * Uses the first TEXT with `suggestionTarget: true` (and a non-empty key); otherwise
 * {@link getFirstTextPropertyKeyForRegionTab} (backwards compatible).
 */
export function getSuggestionTargetTextPropertyKeyForRegionTab(options: {
  faceElements: UVInsertFaceElement[]
  tabIndex: number
}): string | null {
  const { faceElements, tabIndex } = options
  const faces = getFaceElementsForRegionTab({ faceElements, tabIndex })
  for (const face of faces) {
    for (const el of face.elementConfig) {
      if (el.type !== "TEXT" || !isUvInsertUserConfigurableText(el)) {
        continue
      }
      if (el.suggestionTarget === true) {
        return el.lineItemPropertyKey
      }
    }
  }
  return getFirstTextPropertyKeyForRegionTab({ faceElements, tabIndex })
}
